import { FC } from 'react';
import clsx from 'clsx';

interface CounterProps {
  label: string;
  value: number;
  onChange: (value: number) => void;
  min?: number;
  max?: number;
  step?: number;
  error?: string;
  required?: boolean;
  disabled?: boolean;
  className?: string;
}

export const Counter: FC<CounterProps> = ({
  label,
  value,
  onChange,
  min = 0,
  max = 999,
  step = 1,
  error,
  required = false,
  disabled = false,
  className,
}) => {
  const handleDecrement = () => {
    const newValue = value - step;
    if (newValue >= min) {
      onChange(newValue);
    }
  };

  const handleIncrement = () => {
    const newValue = value + step;
    if (newValue <= max) {
      onChange(newValue);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = parseFloat(e.target.value);
    if (!isNaN(newValue) && newValue >= min && newValue <= max) {
      onChange(newValue);
    }
  };

  const isDecrementDisabled = disabled || value <= min;
  const isIncrementDisabled = disabled || value >= max;

  return (
    <div className={clsx('w-full', className)}>
      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
        {label}
        {required && <span className="text-red-500 ml-1">*</span>}
      </label>

      <div className="flex items-center gap-2">
        {/* Bouton - */}
        <button
          type="button"
          onClick={handleDecrement}
          disabled={isDecrementDisabled}
          className={clsx(
            'w-10 h-10 flex items-center justify-center rounded-md border',
            'text-lg font-medium transition-colors',
            'focus:outline-none focus:ring-2 focus:ring-blue-500',
            isDecrementDisabled
              ? 'bg-gray-100 text-gray-400 border-gray-300 cursor-not-allowed dark:bg-gray-700 dark:border-gray-600'
              : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50 dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700'
          )}
          aria-label="Décrémenter"
        >
          −
        </button>

        {/* Input number */}
        <input
          type="number"
          value={value}
          onChange={handleInputChange}
          min={min}
          max={max}
          step={step}
          disabled={disabled}
          className={clsx(
            'w-20 text-center px-3 py-2 border rounded-md',
            'focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500',
            'disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed',
            'dark:bg-gray-800 dark:border-gray-600 dark:text-gray-100',
            'dark:disabled:bg-gray-700',
            error
              ? 'border-red-300 focus:ring-red-500 focus:border-red-500'
              : 'border-gray-300 dark:border-gray-600'
          )}
        />

        {/* Bouton + */}
        <button
          type="button"
          onClick={handleIncrement}
          disabled={isIncrementDisabled}
          className={clsx(
            'w-10 h-10 flex items-center justify-center rounded-md border',
            'text-lg font-medium transition-colors',
            'focus:outline-none focus:ring-2 focus:ring-blue-500',
            isIncrementDisabled
              ? 'bg-gray-100 text-gray-400 border-gray-300 cursor-not-allowed dark:bg-gray-700 dark:border-gray-600'
              : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50 dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700'
          )}
          aria-label="Incrémenter"
        >
          +
        </button>
      </div>

      {/* Error message */}
      {error && (
        <p className="mt-1 text-sm text-red-600 dark:text-red-400">{error}</p>
      )}
    </div>
  );
};
